import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authApi } from '../../lib/api';
import { useSessionStore } from '../../stores/session';
import { useCompanyStore } from '../../stores/company';
import { Field, TextInput } from '../../components/Field';
import { ErrorBanner } from '../../components/ErrorBanner';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const setSession = useSessionStore((s) => s.setSession);
  const setCompanies = useCompanyStore((s) => s.setCompanies);
  const setActiveCompanyId = useCompanyStore((s) => s.setActiveCompanyId);
  const navigate = useNavigate();
  const location = useLocation();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const payload = await authApi.login(email.trim(), password);
      setSession(payload);
      setCompanies(payload.companies);
      setActiveCompanyId(payload.activeCompanyId);
      if (payload.user.mustChangePassword) {
        navigate('/change-password', { replace: true });
      } else {
        const from = (location.state as { from?: Location })?.from?.pathname ?? '/';
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex h-screen items-center justify-center bg-[var(--bg)] px-4">
      <div className="w-full max-w-[340px]">
        <div className="mb-6 flex items-baseline justify-between border-b border-[var(--border)] pb-3">
          <h1 className="text-[16px] font-semibold tracking-tight">Khata</h1>
          <span className="font-mono text-[10.5px] text-[var(--text-faint)]">v0.1.0 · Phase 3</span>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3" noValidate>
          {error ? <ErrorBanner error={error} onDismiss={() => setError(null)} /> : null}

          <Field label="Email" htmlFor="login-email" required>
            <TextInput
              id="login-email"
              type="email"
              autoFocus
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Field>

          <Field label="Password" htmlFor="login-password" required>
            <TextInput
              id="login-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </Field>

          <button
            type="submit"
            disabled={busy}
            className="mt-1 rounded-[3px] border border-[var(--accent)] bg-[var(--accent)] px-3 py-1.5 text-[12.5px] font-medium text-[var(--accent-fg)] disabled:opacity-50"
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>

          <p className="mt-1 text-center text-[11px] text-[var(--text-faint)]">
            Tab between fields, Enter to sign in.
          </p>
        </form>
      </div>
    </div>
  );
}
